/**
 * Base API service with authentication and error handling
 */

import { rememberReturnPath } from '@/utils/returnPath';
import { clearSignedIn, hadSession } from '@/utils/sessionHint';
import type { ApiError, ApiResponse } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * API fetch options
 */
interface ApiFetchOptions extends RequestInit {
  /** Include credentials/auth */
  auth?: boolean;
  /** Custom headers */
  headers?: Record<string, string>;
}

/**
 * Get CSRF token from cookies
 */
function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

/**
 * Base fetch wrapper with error handling and auth
 */
async function apiFetch<T>(
  endpoint: string,
  options: ApiFetchOptions = {}
): Promise<ApiResponse<T>> {
  const { auth = true, headers = {}, ...fetchOptions } = options;

  // Build headers
  const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
  const requestHeaders: Record<string, string> = {
    // FormData bodies need the browser to set the multipart boundary itself
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...headers,
  };

  // Add CSRF token if available
  if (auth) {
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      requestHeaders['X-CSRF-Token'] = csrfToken;
    }
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...fetchOptions,
      headers: requestHeaders,
      credentials: 'include', // Always include for cross-origin cookie support
    });

    // Handle 204 No Content responses (e.g., DELETE)
    if (response.status === 204) {
      return { data: undefined as T };
    }

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType?.includes('application/json')) {
      if (!response.ok) {
        return {
          error: {
            message: `HTTP ${response.status}: ${response.statusText}`,
            code: String(response.status),
          },
        };
      }
      return { data: undefined as T };
    }

    const data = await response.json();

    if (!response.ok) {
      // Handle 401 Unauthorized - redirect to login
      if (response.status === 401) {
        // Don't redirect if already on login/auth pages or public legal pages
        const currentPath = window.location.pathname;
        const noRedirectPaths = [
          '/login', '/register', '/forgot-password', '/auth/reset-password', '/auth/verify-email',
          '/auth/callback', '/auth/error', '/terms-and-conditions', '/privacy-policy', '/about', '/waitlist', '/donate', '/premium', '/events', '/venues', '/'
        ];
        const isAuthPage = noRedirectPaths.some(path => currentPath === path || currentPath.startsWith(path + '/'));

        if (!isAuthPage) {
          // Store the current path to redirect back after login
          rememberReturnPath(currentPath + window.location.search + window.location.hash);
          // Only someone who was signed in on this browser is told their session expired;
          // a first-time visitor following a link just sees the normal sign-in page
          const expired = hadSession();
          clearSignedIn();
          window.location.href = expired ? '/login?session_expired=true' : '/login';
          // Return error but the redirect will happen
        }
      }

      // Handle Pydantic validation errors (detail is array of objects)
      let errorMessage = 'An error occurred';
      if (data.message) {
        errorMessage = data.message;
      } else if (data.detail) {
        if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        } else if (Array.isArray(data.detail)) {
          // Pydantic validation errors
          errorMessage = data.detail
            .map((err: { msg?: string; loc?: string[] }) => {
              const field = err.loc?.slice(1).join('.') || 'field';
              return `${field}: ${err.msg || 'validation error'}`;
            })
            .join(', ');
        } else if (typeof data.detail === 'object') {
          // Structured detail (e.g. 429 quota errors): show its message;
          // the full object (including any fallback) stays on error.details
          const msg = (data.detail as { message?: unknown }).message;
          if (typeof msg === 'string' && msg) {
            errorMessage = msg;
          }
        }
      }
      return {
        error: {
          message: errorMessage,
          code: String(response.status),
          details: data,
        },
      };
    }

    return { data };
  } catch (error) {
    console.error('API fetch error:', error);
    return {
      error: {
        message: error instanceof Error ? error.message : 'Network error',
        code: 'NETWORK_ERROR',
      },
    };
  }
}

/**
 * GET request
 */
export async function apiGet<T>(
  endpoint: string,
  options?: ApiFetchOptions
): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, { ...options, method: 'GET' });
}

/**
 * POST request
 */
export async function apiPost<T, D = unknown>(
  endpoint: string,
  data?: D,
  options?: ApiFetchOptions
): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    ...options,
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PUT request
 */
export async function apiPut<T, D = unknown>(
  endpoint: string,
  data?: D,
  options?: ApiFetchOptions
): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    ...options,
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * PATCH request
 */
export async function apiPatch<T, D = unknown>(
  endpoint: string,
  data?: D,
  options?: ApiFetchOptions
): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, {
    ...options,
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
}

/**
 * DELETE request
 */
export async function apiDelete<T>(
  endpoint: string,
  options?: ApiFetchOptions
): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, { ...options, method: 'DELETE' });
}

/**
 * POST a multipart form (file uploads).
 */
export async function apiUpload<T>(endpoint: string, form: FormData): Promise<ApiResponse<T>> {
  return apiFetch<T>(endpoint, { method: 'POST', body: form });
}

/**
 * POST a multipart form with upload progress (0 to 1). Same cookie and CSRF handling as `apiFetch`;
 * fetch cannot report upload progress, so this uses XMLHttpRequest.
 */
export function apiUploadWithProgress<T>(
  endpoint: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): Promise<ApiResponse<T>> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}${endpoint}`);
    xhr.withCredentials = true;
    const csrf = getCsrfToken();
    if (csrf) xhr.setRequestHeader('X-CSRF-Token', csrf);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) onProgress(event.loaded / event.total);
    };
    xhr.onerror = () => resolve({ error: { message: 'Network error', code: 'NETWORK_ERROR' } });
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve({ data: body as T });
        return;
      }
      const detail = (body as { detail?: unknown; message?: string } | null)?.detail;
      const message = typeof detail === 'string' ? detail : (body as { message?: string } | null)?.message;
      resolve({
        error: {
          message: message || `HTTP ${xhr.status}`,
          code: String(xhr.status),
          details: (body as Record<string, unknown>) ?? undefined,
        },
      });
    };
    xhr.send(form);
  });
}

/**
 * GET a file attachment (CSV/JSON export, templates) as a Blob.
 */
export async function apiDownload(
  endpoint: string
): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, { credentials: 'include' });
    if (!response.ok) {
      return { error: { message: `Download failed (HTTP ${response.status})`, code: String(response.status) } };
    }
    const disposition = response.headers.get('content-disposition') ?? '';
    const filename = disposition.match(/filename="([^"]+)"/)?.[1] ?? 'download';
    return { data: { blob: await response.blob(), filename } };
  } catch (error) {
    return {
      error: { message: error instanceof Error ? error.message : 'Network error', code: 'NETWORK_ERROR' },
    };
  }
}

/**
 * Handle API error response
 */
export function handleApiError(error: ApiError): string {
  return error.message || 'An unexpected error occurred';
}

/**
 * API object wrapper for convenience
 */
export const api = {
  get: apiGet,
  post: apiPost,
  put: apiPut,
  patch: apiPatch,
  delete: apiDelete,
};

/**
 * Return the payload of an ApiResponse, or throw its error message.
 * For services whose callers want a plain value and handle failure with try/catch.
 */
export function unwrap<T>(response: ApiResponse<T>): T {
  if (response.error) {
    throw new Error(response.error.message);
  }
  return response.data as T;
}
